/**
 * The ROLES slice of the admin surface — custom-roles design §1.5/§1.6.
 *
 * A FIFTH api file, for the reason `tax-groups-api.ts` is a fourth: one owner per
 * file, and DTOs transcribed HERE from the API source rather than shared across
 * screens with different jobs. Shapes are cited per type, never guessed:
 *   · `RoleView` / `RoleWriteResult` — apps/api/src/modules/roles/dto/roles.dto.ts
 *   · `RoleWarning`                  — apps/api/src/modules/roles/role-guardrails.ts
 *
 * Every call goes through `apiRequest` — the single-flight refresh mutex (D-web-1).
 * ESLint forbids `fetch` outside `src/lib/api.ts` precisely so a module like this
 * cannot open a second refresh path.
 *
 * ⚠ ALL ROUTES ARE GLOBAL — no `:cc` anywhere. A role is a TEMPLATE; it acquires a
 * country only when `user_country_roles` assigns it. The screens live under
 * `/countries/:cc/admin/roles` for the chrome, exactly as the users screens do, and
 * the `:cc` feeds nothing in this file.
 */

import { ApiRequestError, apiRequest } from '../../lib/api';
import type { PermissionKey, RoleLabel } from '../../lib/permissions';

/* ────────────────────────────────────────────────────────────────── shapes ── */

/** One row of `GET /roles` (roles.dto.ts `RoleView`). */
export interface RoleDto {
  id: number;
  name: RoleLabel;
  /**
   * `'seeded'` — Appendix A owns it. `'custom'` — a human wrote it. The screens BRANCH
   * on this, so it is a closed union, not a free string.
   *
   * ⚠ CORRECTED (owner ruling 2026-08-13). This used to read "rename, delete AND GRANT
   * EDITS are refused (`SEEDED_ROLE_LOCKED`) and Clone is the modify path". Grant edits
   * are no longer refused: a group admin may replace a seeded role's `permissions`, and
   * the server pins the role so the next seed run stops converging it. Rename and delete
   * are still refused to everybody, and Clone is still the path for those.
   */
  origin: 'seeded' | 'custom';
  description: string | null;
  /** Ascending by key — the server promises the order, so two roles diff by eye. */
  permissions: PermissionKey[];
  /** `user_country_roles` rows pointing here, across every country. */
  holders: number;
}

/**
 * One §1.7 composition warning (`role-guardrails.ts`).
 *
 * The `message` is SERVER-AUTHORED and rendered verbatim. That is deliberate: the
 * sentences name a consequence measured in the API's own code paths ("they would
 * confirm a price they cannot read"), and a client paraphrase would drift from the
 * rule it describes the first time either changed.
 */
export interface RoleWarningDto {
  code: string;
  message: string;
  permissions: PermissionKey[];
}

/**
 * The code a role write is refused with when its §1.7 warnings have not been confirmed.
 * Transcribed from `ErrorCode` in the API rather than imported: this file transcribes
 * its contracts, and a value import from the shared package is the rollup hazard
 * `catalogue-api.ts` documents.
 */
export const ROLE_WARNINGS_UNACKNOWLEDGED = 'ROLE_WARNINGS_UNACKNOWLEDGED';

/** The warnings carried by that refusal, so the confirm needs no second round trip. */
export function unacknowledgedWarnings(error: unknown): RoleWarningDto[] | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (error.code !== ROLE_WARNINGS_UNACKNOWLEDGED) return null;
  const list = (error.details as { warnings?: unknown } | undefined)?.warnings;
  return Array.isArray(list) ? (list as RoleWarningDto[]) : [];
}

/** `POST`/`PATCH`/clone answer — the write HAPPENED; warnings ride the 200. */
export interface RoleWriteResultDto {
  role: RoleDto;
  warnings: RoleWarningDto[];
}

export interface CreateRoleBody {
  name: string;
  description?: string;
  permissions: PermissionKey[];
  /** §1.7's explicit confirm — see {@link UpdateRoleBody.acknowledge_warnings}. */
  acknowledge_warnings?: boolean;
}

/** Every field optional; an omitted field is untouched, `permissions` REPLACES. */
export interface UpdateRoleBody {
  name?: string;
  description?: string | null;
  permissions?: PermissionKey[];
  /**
   * "The warnings were shown and the operator confirmed them" (design §1.7).
   *
   * Sent ONLY after a 422 `ROLE_WARNINGS_UNACKNOWLEDGED` has been rendered and confirmed
   * — never speculatively on the first attempt. Setting it by default would make the
   * server-side confirm exactly the decoration it exists to stop being.
   */
  acknowledge_warnings?: boolean;
}

/* ─────────────────────────────────────────────────────────────────── calls ── */

export async function fetchRoles(signal?: AbortSignal): Promise<RoleDto[]> {
  const body = await apiRequest<{ roles: RoleDto[] }>('/roles', { signal });
  return body.roles;
}

export async function createRole(body: CreateRoleBody): Promise<RoleWriteResultDto> {
  return apiRequest<RoleWriteResultDto>('/roles', { method: 'POST', body });
}

export async function updateRole(
  id: number,
  body: UpdateRoleBody,
): Promise<RoleWriteResultDto> {
  return apiRequest<RoleWriteResultDto>(`/roles/${String(id)}`, { method: 'PATCH', body });
}

export async function cloneRole(id: number): Promise<RoleWriteResultDto> {
  return apiRequest<RoleWriteResultDto>(`/roles/${String(id)}/clone`, { method: 'POST' });
}

export async function deleteRole(id: number): Promise<void> {
  await apiRequest<void>(`/roles/${String(id)}`, { method: 'DELETE' });
}

/* ──────────────────────────────────────────────────────────────── query keys ── */

/**
 * Nested under `['roles']`, and the USERS screens invalidate this prefix too.
 *
 * That is the invalidation contract, not a coincidence: the role assignment picker
 * on the user screens reads this exact list, so a role authored here is assignable
 * without a reload, and a role deleted here disappears from the picker rather than
 * sitting there until the next 422.
 */
export const roleKeys = {
  root: () => ['roles'] as const,
  list: () => ['roles', 'list'] as const,
};
